import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import styled from '@emotion/styled';
import { Button, Chip, Grid, GridProps, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { Order, TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2, TitleMetadataItem } from '@/redux/slices/titleV2Slice.types';
import { setActiveTitle, getTitleDetails } from '@/redux/slices/titleSlice';
import { fetchTitleV2, setActiveTitleId } from '@/redux/slices/titleV2Slice';
import { selectSearchValue, selectSelectedHierarchy } from '@/redux/selectors/orderPlacementSelectors';
import { selectPackagingActiveTemplate } from '@/redux/selectors/packagingSelectors';
import { selectTitle, selectVersionNamesByParentIds, selectVersionsByParentIds } from '@/redux/selectors/titleSelectors';
import { selectActiveTitleId, selectActiveVersionsByTitleId, selectRootTitle } from '@/redux/selectors/titleV2Selectors';
import { selectCanCreateEmbargoedTitleOrderPending, selectCanCreateEmbargoedTitleOrderProgress } from '@/redux/selectors/authSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import useCurrentBreakpoint from '@/components/_hooks/useCurrentBreakpoint';
import PolarisIcon from '@/components/_common/PolarisIcon';
import OnOffToggle from '@/components/_common/OnOffToggle';
import TitleVersionChip from '@/components/_common/TitleVersionChip';
import ListOverflowBadge from '@/components/_common/ListOverflowBadge/ListOverflowBadge';
import ExpandedVersionRow from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/ExpandedVersionRow';
import TitleBrowserAccordion from '@/components/OrderPlacement/OPStep2FileSelection/TitleBrowser/TitleBrowserAccordion';

type TitleBrowserEntityProps = {
  entity: TitleEntity | TitleEntityV2;
  metadata?: TitleMetadataItem;
  defaultExpanded?: boolean;
};

const TitleBrowserEntity = ({ entity, metadata, defaultExpanded = false }: TitleBrowserEntityProps) => {
  if (entity.entity_type === 'version') return null;
  const dispatch = useAppDispatch();
  const isTitleV2 = useConfigValue(['titleConfig', 'v2']);
  const { expandedVersionRows, searchTermFilter, disableAddVersionFn, renderMetadata, checkEmbargoedVersions } =
    useConfigValue(['orderPlacementConfig']) || {};
  const canCreateEmbargoedTitleOrderPending = useAppSelector(selectCanCreateEmbargoedTitleOrderPending);
  const canCreateEmbargoedTitleOrderProgress = useAppSelector(selectCanCreateEmbargoedTitleOrderProgress);
  const parentTitle = isTitleV2 ? useAppSelector(selectRootTitle) : useAppSelector(selectTitle);
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const activeVersions = useAppSelector((state) => selectActiveVersionsByTitleId(state, entity.id));
  const versionNamesByParentIds = isTitleV2
    ? useMemo(() => {
        const versionNamesByParentId: Record<number, string[]> = {};
        activeVersions?.forEach((version) => {
          if (!version.parent) return;
          if (!versionNamesByParentId[version.parent]) {
            versionNamesByParentId[version.parent] = [];
          }
          versionNamesByParentId[version.parent].push(version.name as string);
        });
        return versionNamesByParentId;
      }, [activeVersions, entity.id])
    : useAppSelector(selectVersionNamesByParentIds);
  const versionsByParentIds = isTitleV2
    ? useMemo(() => {
        const versionsByParentId: Record<string, TitleEntity[]> = {};
        activeVersions?.forEach((version) => {
          if (!version.parent) return;
          if (!versionsByParentId[version.parent]) {
            versionsByParentId[version.parent] = [];
          }
          versionsByParentId[version.parent].push(version);
        });
        return versionsByParentId;
      }, [activeVersions, entity.id])
    : useAppSelector(selectVersionsByParentIds);

  const activeAssemblyTemplate = useAppSelector(selectPackagingActiveTemplate);
  const searchValue = useAppSelector(selectSearchValue);
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const formikContext: undefined | Partial<Order> = activeAssemblyTemplate ? undefined : useFormikContext();
  const [displayedVersions, setDisplayedVersions] = useState<TitleEntity[]>([]);
  const [searchedVersion, setSearchedVersion] = useState<TitleEntity[] | undefined>(undefined);
  const [viewInactive, setToggleViewInactive] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { sequence_order, name, entity_type } = entity;
  const flowType = formikContext ? formikContext.values?.jobs?.[0]?.profile?.data?.workflow_type : null;
  const isVersionBased =
    !activeAssemblyTemplate && formikContext && formikContext.values.jobs
      ? !flowType || ['version_based', 'package', 'bulk', 'version_based_bundle'].indexOf(flowType) !== -1
      : true;
  const currentBreakpoint = useCurrentBreakpoint();
  const headerOrientation = useMemo<{
    direction: CSSProperties['flexDirection'];
    justifyContent: CSSProperties['justifyContent'];
    alignItems: CSSProperties['alignItems'];
    xs: GridProps['xs'];
  }>(() => {
    return {
      direction: ['lg', 'xl'].includes(currentBreakpoint) ? 'row' : 'column',
      justifyContent: ['lg', 'xl'].includes(currentBreakpoint) ? 'space-between' : 'flex-start',
      alignItems: ['lg', 'xl'].includes(currentBreakpoint) ? 'center' : 'flex-start',
      xs: ['lg', 'xl'].includes(currentBreakpoint) ? 6 : 12
    };
  }, [currentBreakpoint]);

  // if the client doesn't use this field, do not show the toggle
  const shouldFilterInactive = versionsByParentIds[entity.id]?.every((version) => version.meta?.is_active !== undefined);

  const disableAddVersion = useCallback(
    (version: TitleEntity): boolean => {
      const disable = disableAddVersionFn
        ? disableAddVersionFn(entity, selectedHierarchy, {
            canCreateEmbargoedTitleOrderPending,
            canCreateEmbargoedTitleOrderProgress,
            version
          })
        : false;

      return !versionNamesByParentIds[entity.id]?.includes(version.name || '') || version.disabled || disable;
    },
    [
      versionNamesByParentIds,
      entity,
      selectedHierarchy,
      disableAddVersionFn,
      canCreateEmbargoedTitleOrderPending,
      canCreateEmbargoedTitleOrderProgress
    ]
  );

  const handleBrowseInventory = () => {
    console.log('browsing inventory for', entity);
    if (isTitleV2) {
      if (activeTitleId !== entity.id) {
        dispatch(fetchTitleV2(entity.id));
      }
      dispatch(setActiveTitleId(entity.id));
      dispatch(getTitleDetails(entity.id));
      return;
    }

    dispatch(setActiveTitle(entity.id));
    dispatch(getTitleDetails(entity.id));
  };

  const toggleActive = () => {
    setToggleViewInactive(!viewInactive);
  };

  useEffect(
    function handleDisplayedVersions() {
      let activeVersions = versionsByParentIds[entity.id];
      if (shouldFilterInactive && !viewInactive) {
        activeVersions = versionsByParentIds[entity.id]?.filter((x) => x.meta?.is_active);
      }
      if (typeof searchTermFilter === 'function') setSearchedVersion(searchTermFilter(activeVersions, searchValue));
      setDisplayedVersions(activeVersions);
    },
    [viewInactive, parentTitle?.id, versionsByParentIds]
  );

  const disableExpand = !isVersionBased || !displayedVersions?.length;
  const versionChipList = Array.isArray(searchedVersion) && searchedVersion.length ? searchedVersion : displayedVersions;

  return activeAssemblyTemplate ? (
    <NonAccordionDiv>
      <SummaryLine>
        {entity_type && entity_type === 'episode' && (
          <span style={{ paddingRight: 5 }}>E{isNaN(Number(sequence_order)) ? sequence_order : Number(sequence_order) || '?'}:</span>
        )}
        {name}
        <div>
          <span style={{ margin: '0 10px' }}>|</span>
          <span>ID: {entity.external_id || entity.id}</span>
        </div>
      </SummaryLine>
      <Button variant="outlined" color="secondary" sx={{ ml: 1, whitespace: 'nowrap' }} onClick={handleBrowseInventory}>
        View Inventory
      </Button>
    </NonAccordionDiv>
  ) : (
    <TitleBrowserAccordion
      disableExpand={disableExpand}
      defaultExpanded={defaultExpanded}
      expandedCallback={setExpanded}
      title={
        <Grid
          container
          alignItems={headerOrientation.alignItems}
          justifyContent={headerOrientation.justifyContent}
          flexWrap="nowrap"
          flexDirection={headerOrientation.direction}
          sx={{ minHeight: 65 }}
        >
          <Grid item xs={headerOrientation.xs}>
            <Typography sx={{ fontSize: '1.1rem' }}>
              {entity_type && entity_type === 'episode' && (
                <span style={{ opacity: 0.6, paddingRight: 5 }}>
                  E{isNaN(Number(sequence_order)) ? sequence_order : Number(sequence_order) || '?'}:
                </span>
              )}
              {name}
              <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                ID: {entity.external_id || entity.id}
              </Typography>
            </Typography>
          </Grid>
          {metadata && renderMetadata(metadata)}
          <Grid item xs={headerOrientation.xs} sx={{ width: headerOrientation.xs === 12 ? '100%' : 'auto' }}>
            {shouldFilterInactive && expanded && (
              <StyledOnOffToggleWrapper breakpoint={currentBreakpoint}>
                <OnOffToggle on={viewInactive} onToggle={toggleActive} label="View inactive versions" />
              </StyledOnOffToggleWrapper>
            )}
            {!expanded && (
              <StyledActionGrid isFullRow={headerOrientation.xs === 12}>
                {isVersionBased ? (
                  versionsByParentIds[entity.id]?.length > 0 ? (
                    <>
                      <StyledDiv>
                        {versionChipList?.length && (
                          <ListOverflowBadge
                            list={versionChipList.map((version) => {
                              const isEmbargoed = checkEmbargoedVersions && version.is_embargoed && disableAddVersion(version);
                              return (
                                <span key={version.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  <TitleVersionChip
                                    tooltip={isEmbargoed ? 'Embargoed cannot be selected' : undefined}
                                    version={version}
                                    disabled={disableAddVersion(version)}
                                    withTooltip
                                    tooltipSource="global-channel-ids"
                                  />
                                  {isEmbargoed && <span style={{ color: '#ed6c02', fontWeight: 600, fontSize: '0.9em', marginLeft: 4 }}>⚠️</span>}
                                </span>
                              );
                            })}
                            delineated={false}
                          />
                        )}
                      </StyledDiv>
                    </>
                  ) : (
                    <Typography variant="caption">No versions</Typography>
                  )
                ) : (
                  <Chip
                    label="Browse Inventory"
                    color="primary"
                    variant="outlined"
                    icon={<PolarisIcon icon="search_folders" />}
                    sx={{ pl: 0.75 }}
                    onClick={handleBrowseInventory}
                  />
                )}
              </StyledActionGrid>
            )}
          </Grid>
        </Grid>
      }
    >
      {!disableExpand && (
        <Grid container>
          {expandedVersionRows ? (
            expandedVersionRows(displayedVersions, searchedVersion, isTitleV2, selectedHierarchy, {
              canCreateEmbargoedTitleOrderPending,
              canCreateEmbargoedTitleOrderProgress
            })
          ) : (
            <ExpandedVersionRow versions={displayedVersions} />
          )}
        </Grid>
      )}
    </TitleBrowserAccordion>
  );
};

export default TitleBrowserEntity;

const SummaryLine = styled.div(({ theme }) => ({
  fontSize: '1.2rem',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  marginTop: theme.spacing(1),
  div: {
    opacity: 0.5
  }
}));

const NonAccordionDiv = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.primary.main + '4a',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  display: 'flex',
  flexDirection: 'row',
  height: 50,
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2)
}));

const StyledDiv = styled.div(() => ({
  overflow: 'hidden',
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'nowrap',
  marginLeft: 'auto'
}));

const StyledActionGrid = styled(Grid, {
  shouldForwardProp: (prop) => !['isFullRow'].includes(prop)
})<{ isFullRow: boolean }>(({ theme, isFullRow }) => ({
  alignItems: 'center',
  justifyContent: isFullRow ? 'flex-start' : 'flex-end',
  display: 'flex',
  flexWrap: 'nowrap',
  paddingRight: theme.spacing(1),
  minHeight: '45px',

  '& > div': {
    marginLeft: 0,
    width: '100%',

    '& > div': {
      justifyContent: isFullRow ? 'flex-start' : 'flex-end'
    }
  }
}));

const StyledOnOffToggleWrapper = styled.div<{ breakpoint: string }>(({ theme, breakpoint }) => ({
  justifyContent: ['lg', 'xl'].includes(breakpoint) ? 'flex-end' : 'flex-start',
  alignItems: 'center',
  display: 'flex',
  '&': ['lg', 'xl'].includes(breakpoint)
    ? {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(2)
      }
    : {}
}));
